import { CheckIcon } from "@xola/icons";
import { Button, Table, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { capitalize, first, sumBy } from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ExperiencePurchaseItem, Gratuity, GratuityStatus, ReferenceObject, Transaction, User } from "types";
import shallow from "zustand/shallow";
import { Currency } from "../../Common/Currency";
import { NoTranslate } from "../../Common/NoTranslate";
import { ProductImage } from "../../Common/ProductImage";
import { GratuityItemName } from "./GratuityItemName";
import { ReconcileGratuitiesModal } from "./ReconcileGratuitiesModal";
import { AutoTooltipContainer } from "@/components/Common/AutoTooltipContainer";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useResource } from "@/hooks/useResource";
import { useModal } from "@/hooks/useVisibility";
import { useGratuitiesFilterStore } from "@/stores/reports/gratuitiesFilterStore";

export const GratuitiesTable = () => {
    // Using getters for `from` and `to` here will recalculate now() in case these values are undefined causing an infinity loop.
    const [from, to] = useGratuitiesFilterStore((state) => [state.from, state.to], shallow);
    const filters = useGratuitiesFilterStore((state) => state.filters);
    const dateParams = useGratuitiesFilterStore((state) => state.getDateParams());

    const params: any = { ...dateParams, expand: "transaction,references.user_profile,references.event" };
    if (filters.status.selected.length > 0) {
        params["status[in]"] = filters.status.selected.join(",");
    }

    if (filters.guides.selected.length > 0) {
        params["references[in]"] = filters.guides.selected.join(",");
    }

    const { data: gratuities, isLoading, refetch } = useInfiniteCollection<Gratuity>("gratuities", params);

    const reconcileModal = useModal(false);
    const [selectedGratuities, setSelectedGratuities] = useState<Gratuity[]>([]);

    useEffect(() => {
        setSelectedGratuities([]);
    }, [to, from, filters]);

    const selectGratuity = (gratuity: Gratuity) => {
        const hasGratuity = selectedGratuities.some((g) => g.id === gratuity.id);
        if (hasGratuity) {
            setSelectedGratuities(selectedGratuities.filter((g) => g.id !== gratuity.id));
        } else {
            setSelectedGratuities([...selectedGratuities, gratuity]);
        }
    };

    const handleClose = () => {
        refetch();
        setSelectedGratuities([]);
        reconcileModal.hide();
    };

    const gratuitiesWithTransaction = gratuities.filter((gratuity) => gratuity.transaction);

    return (
        <>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>Item</Table.Header>
                        <Table.Header>Guides Name(s)</Table.Header>
                        <Table.Header>Payment Date</Table.Header>
                        <Table.Header>Event Date</Table.Header>
                        <Table.Header>Event Time</Table.Header>
                        <Table.Header>Gross</Table.Header>
                        <Table.Header>Processing Fee</Table.Header>
                        <Table.Header>Net</Table.Header>
                        <Table.Header>Note</Table.Header>
                        <Table.Header>Source</Table.Header>
                        <Table.Header>Method</Table.Header>
                        <Table.Header> </Table.Header>
                    </Table.Row>
                </Table.Head>

                <Table.Body isStriped>
                    {gratuitiesWithTransaction.map((gratuity) => {
                        const isSelected = selectedGratuities.some((g) => g.id === gratuity.id);
                        return (
                            <GratuityItem
                                key={gratuity.id}
                                isSelected={isSelected}
                                gratuity={gratuity}
                                selectGratuity={selectGratuity}
                            />
                        );
                    })}
                </Table.Body>
            </Table>

            {gratuities.length === 0 && !isLoading ? (
                <div className="my-2 text-center text-base text-gray-dark">
                    No gratuity data found. Try choosing a different date range or filters
                </div>
            ) : null}

            {selectedGratuities.length > 0 && (
                <div className="mt-4 text-right">
                    <Button onClick={reconcileModal.show}>
                        <div className="mr-1">Pay</div>
                        <Currency>{sumBy(selectedGratuities, "transaction.items[0].net")}</Currency>
                    </Button>
                </div>
            )}
            <ReconcileGratuitiesModal
                selectedGratuities={selectedGratuities}
                isOpen={reconcileModal.isVisible}
                onClose={handleClose}
            />
        </>
    );
};

interface GratuityItemProps {
    gratuity: Gratuity;
    selectGratuity: (gratuity: Gratuity) => void;
    isSelected: boolean;
}

const GratuityItem = ({ gratuity, selectGratuity, isSelected }: GratuityItemProps) => {
    const history = useHistory();

    const gratuityEventReference = gratuity.references.find((reference) => reference.object === ReferenceObject.EVENT);
    const gratuityPurchaseReference = gratuity.references.find((reference) => {
        return reference.object === ReferenceObject.PURCHASE_ITEM;
    });

    const { data: purchaseItem } = useResource<ExperiencePurchaseItem>([
        "purchaseItems",
        gratuityPurchaseReference?.id,
    ]);

    const gratuityTransactionItem = first(gratuity.transaction.items);
    if (!gratuityTransactionItem) {
        return null;
    }

    const handleOpenPurchase = () => {
        if (purchaseItem) {
            history.push(`/purchases/${purchaseItem?.purchase?.id}`);
        }
    };

    return (
        <Table.Row
            className={clsx("cursor-pointer hover:bg-primary-lighter", { "bg-primary-lighter": isSelected })}
            onClick={() => (gratuity.status === GratuityStatus.RECONCILED ? null : selectGratuity(gratuity))}
        >
            <Table.Cell>
                <div className="w-15" onClick={handleOpenPurchase}>
                    <Tooltip content={<NoTranslate>{gratuityEventReference?.title}</NoTranslate>}>
                        {gratuityTransactionItem && (
                            <ProductImage product={gratuityTransactionItem.orderItem.product} size="small" />
                        )}
                    </Tooltip>
                </div>
            </Table.Cell>
            <Table.Cell>
                <GratuityItemName gratuity={gratuity} />
            </Table.Cell>
            <Table.Cell className="no-translate">
                {formatDate(gratuity.transaction.createdAt, DateFormat.DATE_SHORT)}
            </Table.Cell>
            <Table.Cell className="no-translate">
                {gratuityEventReference ? formatDate(gratuityEventReference.actualStart, DateFormat.DATE_SHORT) : "--"}
            </Table.Cell>
            <Table.Cell className="no-translate">
                {gratuityEventReference ? formatDate(gratuityEventReference.actualStart, DateFormat.TIME) : "--"}
            </Table.Cell>
            <Table.Cell>
                <Currency>{gratuityTransactionItem.gross}</Currency>
            </Table.Cell>
            <Table.Cell>
                <Currency>{gratuityTransactionItem.commission}</Currency>
            </Table.Cell>
            <Table.Cell>
                <Currency>{gratuityTransactionItem.net}</Currency>
            </Table.Cell>
            <Table.Cell>
                <div className="w-40">
                    <AutoTooltipContainer tooltipContent={gratuity.comment}>
                        <span className="no-translate">{gratuity.comment}</span>
                    </AutoTooltipContainer>
                </div>
            </Table.Cell>
            <Table.Cell>{capitalize(gratuity.transaction.source) ?? "--"}</Table.Cell>
            <Table.Cell>{capitalize(gratuity.transaction.method) ?? "--"}</Table.Cell>
            <Table.Cell>
                <div className="w-4">
                    {isSelected && <CheckIcon color="text-black" size="medium" />}
                    {gratuity.status === GratuityStatus.RECONCILED && (
                        <Tooltip content={<ReconcileDetails id={gratuity.transaction.reconcile.id} />}>
                            <CheckIcon color="text-black" size="medium" />
                        </Tooltip>
                    )}
                </div>
            </Table.Cell>
        </Table.Row>
    );
};

const ReconcileDetails = ({ id }: { id: ID }) => {
    const { data: transaction } = useResource<Transaction>(["transactions", id]);
    const { data: user } = useResource<User>(["users", transaction?.createdBy.id]);
    if (!transaction) {
        return null;
    }

    return (
        <>
            Reconciled by <var data-var="user">{user?.name}</var> at{" "}
            <var data-var="createdAt">{formatDate(transaction?.createdAt, DateFormat.DATE_LONG)}</var>
        </>
    );
};
